import { motion } from "framer-motion"
import type { SVGProps } from "react"
import { useLogoAnimation } from "../../hooks/use-logo-animation"
import { LogoSVGDefs } from "./LogoSVGDefs"

/**
 * Animated "LIVE" word-mark used across the landing page.
 *
 * The green ⌃ (buy) and red ⌄ (sell) carets react to two global custom events:
 *   • `tradeActivity`   – random mash of both buttons (buy & sell)
 *   • `platformAdvance` – single press of the green caret in-sync with the
 *                         rotating platform card.
 */
export function LandingLogoLive({ className = "", ...props }: SVGProps<SVGSVGElement>) {
  const { greenControls, redControls } = useLogoAnimation()

  // Use unique IDs to avoid Safari filter conflicts
  const uniqueId = Math.random().toString(36).substring(7)
  const idPrefix = `landing-logo-live-${uniqueId}`

  return (
    <svg
      width="361"
      height="128"
      viewBox="0 0 361 128"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label="LIVE wordmark"
      className={className}
      {...props}
    >
      <title>LIVE wordmark</title>
      <g clipPath={`url(#${idPrefix}-clip0_2688_3499)`}>
        {/* Carets - removed filters for Safari compatibility */}
        <motion.path
          d="M119.24 48.03L117.23 54.29H64.7502L62.7402 48.03L90.9402 6.75L119.24 48.03Z"
          fill="#22FF65"
          style={{
            transformOrigin: "50% 50%",
            filter: "drop-shadow(0 0 8px rgba(34, 255, 101, 0.6))",
            WebkitFilter: "drop-shadow(0 0 8px rgba(34, 255, 101, 0.6))",
          }}
          initial={{ scale: 1, opacity: 1 }}
          animate={greenControls}
        />
        <motion.path
          d="M112.39 114.97L84.3799 73.6897L86.3899 67.4297H138.48L140.49 73.6897L112.38 114.97H112.39Z"
          fill="#FF2D55"
          style={{
            transformOrigin: "50% 50%",
            filter: "drop-shadow(0 0 8px rgba(255, 45, 85, 0.6))",
            WebkitFilter: "drop-shadow(0 0 8px rgba(255, 45, 85, 0.6))",
          }}
          initial={{ scale: 1, opacity: 1 }}
          animate={redControls}
        />

        {/* Remaining static letterforms without filters for Safari compatibility */}
        <path d="M28.59 0H0V103.9H20.4L28.59 75.1V0Z" fill="#F3F8F6" />
        <path d="M279.11 127.748H354.15L360.57 103.898H285.64L279.11 127.748Z" fill="#F3F8F6" />
        <path
          d="M293.72 75.1H344.42L350.98 52.04H285.73L293.73 23.86H354.3L360.69 0H274.66L265.06 33.65V103.9H285.74L293.72 75.1Z"
          fill="#F3F8F6"
        />
        <path d="M167.15 0H137.56L170.21 103.9H197.67L191.46 83.67L167.15 0Z" fill="#F3F8F6" />
        <path d="M190.62 127.74H215.97L255.15 0H227.21L190.62 127.74Z" fill="#F3F8F6" />
        <path d="M13.8799 127.748H77.3399L83.8899 103.898L20.3999 103.908L13.8799 127.748Z" fill="#F3F8F6" />
      </g>
      <LogoSVGDefs idPrefix={idPrefix} />
    </svg>
  )
}
